import { DEFAULT_LOCALE } from '../constants';
import type { BasePrice, BaseSavedPrice } from '../graphql/v2';

// [ECOM-41] @TODO: Our app still uses 'jp' on the url, so we need to reparse it
// until we finally migrate all codebase to standard 'ja'
export type Language = 'en' | 'de' | 'fr' | 'zh' | 'es' | 'ja';

export type UrlLanguage = Omit<Language, 'ja'> | 'jp';

export const getLocaleFromPathname = (pathname: string): string =>
  pathname.replace(/^\//, '').split('/')[0];

export const getUrlLocale = (locale: string): UrlLanguage =>
  locale === 'ja' ? 'jp' : locale;

export const getISOLocale = (locale: string): Language => {
  const ISOLocale = locale === 'jp' ? 'ja' : (locale as Language);
  return supportedLanguagesFromUrl.includes(locale)
    ? ISOLocale
    : DEFAULT_LOCALE;
};

export const supportedLanguagesFromUrl: UrlLanguage[] = [
  'en',
  'de',
  'es',
  'fr',
  'jp', // this is not a standard locale... old NI problem
  'zh',
];

export const supportedLanguages: Language[] = [
  // ISO-standard locales
  'en',
  'de',
  'es',
  'fr',
  'ja',
  'zh',
];

export function isValidUrlLanguage(
  lang: string | UrlLanguage,
): lang is UrlLanguage {
  return lang?.length > 0 && supportedLanguagesFromUrl.includes(lang);
}

export function replaceLanguageInUrl(url: string, newLang: UrlLanguage) {
  const parsedUrl = new URL(url);

  // pathname into segments: "/en/pricing/" -> ["en", "pricing"]
  const segments = parsedUrl.pathname.split('/').filter(Boolean);

  if (isValidUrlLanguage(segments[0])) {
    (segments[0] as UrlLanguage) = newLang; // we are sure now
    parsedUrl.pathname = `/${segments.join('/')}`;
  }

  return parsedUrl.toString();
}

export type Translation = {
  language?: Language;
  country?: string;
  messages?: { [key: string]: string };
};

const CODE_CURRENCIES = ['AUD', 'CAD'];

export const tPrice = ({
  price,
  translation,
}: {
  price: BasePrice | BaseSavedPrice;
  translation: Translation;
}) => {
  const locale = `${translation.language}-${translation.country}`;

  const formattedPrice = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: price.currency,
    currencyDisplay: CODE_CURRENCIES.includes(price.currency)
      ? 'code'
      : undefined,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price.value);

  if (formattedPrice.includes('AUD')) {
    return formattedPrice.replace('AUD', 'AU $');
  }

  if (formattedPrice.includes('CAD')) {
    return formattedPrice.replace('CAD', 'CA $');
  }

  return formattedPrice;
};

export const t = (
  translation: Translation,
  id: string,
  config?: {
    fallback?: string;
    values?: Record<string, string>;
  },
): string => {
  const template = translation.messages?.[id] || config?.fallback;

  if (!template) {
    throw new Error(
      `Invalid translation key or fallback provided for id: ${id}`,
    );
  }

  return config?.values
    ? template.replace(
        /{(\w+)}/g,
        (match: string, key: string) => config?.values?.[key] || match,
      )
    : template;
};
